import {
    Add,
    Close,
    Delete,
    Edit,
    ExpandMore,
    FirstPage,
    Info,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    LastPage,
    Remove,
    Visibility,
} from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    Icon,
    IconButton,
    MenuItem,
    Modal,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import "./Table.css";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import ChikoIcon from "../static/chiko-icon.webp";
import { TimePicker } from "../components/TimePicker";

const CampaignPage = () => {
    const backendEndpoint = process.env.REACT_APP_BACKEND_ENDPOINT;

    const [meta, setMeta] = useState(null);
    const [campaigns, setCampaigns] = useState([]);

    const [deleteId, setDeleteId] = useState(null);
    const [open, setOpen] = useState(false);

    const executionHoursDefaultValue = useMemo(
        () => ({
            sunday: {
                active: true,
                openHour: "00:00",
                closeHour: "23:45",
            },
            monday: {
                active: true,
                openHour: "00:00",
                closeHour: "23:45",
            },
            tuesday: {
                active: true,
                openHour: "00:00",
                closeHour: "23:45",
            },
            wednesday: {
                active: true,
                openHour: "00:00",
                closeHour: "23:45",
            },
            thursday: {
                active: true,
                openHour: "00:00",
                closeHour: "23:45",
            },
            friday: {
                active: true,
                openHour: "00:00",
                closeHour: "23:45",
            },
            saturday: {
                active: true,
                openHour: "00:00",
                closeHour: "23:45",
            },
        }),
        [],
    );

    const [validate, setValidate] = useState(false);
    const [campaignId, setCampaignId] = useState(null);
    const [active, setActive] = useState(true);
    const [campaignName, setCampaignName] = useState("");
    const [campaignType, setCampaignType] = useState("");
    const [channelId, setChannelId] = useState(null);
    const [channelType, setChannelType] = useState("");
    const [content1, setContent1] = useState(null);
    const [content2, setContent2] = useState(null);
    const [content3, setContent3] = useState(null);
    const [slowPacedExecution, setSlowPacedExecution] = useState(false);
    const [hasFollowUp, setHasFollowUp] = useState(true);
    const [misfireExecution, setMisfireExecution] = useState(true);
    const [executionHours, setExecutionHours] = useState(
        executionHoursDefaultValue,
    );

    const [variablesChangeDialogOpen, setVariablesChangeDialogOpen] =
        useState(false);
    const [variablesChangeAcknowledged, setVariablesChangeAcknowledged] =
        useState(false);

    const extractBodyVariables = (texts) =>
        Array.from(
            new Set(
                texts
                    .filter((text) => text !== null)
                    .flatMap(
                        (text) =>
                            Array.from(text.matchAll(/{(\w+)}/g)).map(
                                (match) => match[1],
                            ) || [],
                    ),
            ),
        ).sort();

    const isNew = campaignId === null;
    const selectedChannel =
        channelId === null
            ? null
            : meta?.integratedChannels.find(
                  (channel) => channel.channelId === channelId,
              );

    const extractVariables = useCallback(
        (rawContents) => {
            if (!selectedChannel?.templates) {
                return { body: extractBodyVariables(rawContents) };
            }

            return Object.fromEntries(
                [
                    ...new Set(
                        rawContents
                            .filter(Boolean)
                            .map((content) => content.split(":")[1]),
                    ),
                ]
                    .sort()
                    .map((id) => {
                        const template = selectedChannel.templates.find(
                            (template) => template.id === id,
                        );
                        const definitions = template?.variableDefinitions;
                        return [
                            id,
                            {
                                header: definitions?.header ?? null,
                                body: definitions
                                    ? [...definitions.body].sort()
                                    : extractBodyVariables([
                                          template?.text || "",
                                      ]),
                                buttons: definitions?.buttons || [],
                            },
                        ];
                    }),
            );
        },
        [selectedChannel],
    );

    const resolveContentTexts = useCallback(
        (rawContent1, rawContent2, rawContent3) => {
            if (selectedChannel?.templates) {
                return [
                    rawContent1 && rawContent1 !== ""
                        ? selectedChannel.templates.find(
                              (template) =>
                                  template.id === rawContent1.split(":")[1],
                          )?.text || ""
                        : "",
                    rawContent2 && rawContent2 !== ""
                        ? selectedChannel.templates.find(
                              (template) =>
                                  template.id === rawContent2.split(":")[1],
                          )?.text || ""
                        : rawContent2,
                    rawContent3 && rawContent3 !== ""
                        ? selectedChannel.templates.find(
                              (template) =>
                                  template.id === rawContent3.split(":")[1],
                          )?.text || ""
                        : rawContent3,
                ];
            }
            return [rawContent1 || "", rawContent2, rawContent3];
        },
        [selectedChannel],
    );

    const contents = resolveContentTexts(content1, content2, content3).filter(
        (content) => content !== null,
    );
    const variables = extractVariables([content1, content2, content3]);
    const variableTypeLabels = {
        text: "Texto",
        image: "Imagem",
        video: "Vídeo",
        document: "Documento",
        url: "Link dinâmico",
        copy_code: "Copiar código",
    };
    const templateVariableGroups = [content1, content2, content3]
        .map((content, index) => {
            if (!content) return null;
            const templateId = content.split(":")[1];
            const template = selectedChannel?.templates?.find(
                (item) => item.id === templateId,
            );
            const definition = variables[templateId];
            if (!definition) return null;

            const fields = [];
            if (definition.header) {
                fields.push(
                    `Cabeçalho: ${variableTypeLabels[definition.header] || definition.header}`,
                );
            }
            definition.body.forEach((name) => fields.push(`Corpo: ${name}`));
            definition.buttons.forEach((type, buttonIndex) => {
                if (type) {
                    fields.push(
                        `Botão ${buttonIndex + 1}: ${variableTypeLabels[type] || type}`,
                    );
                }
            });

            return {
                label: `Template ${index + 1}${template?.name ? ` — ${template.name}` : ""}`,
                fields,
            };
        })
        .filter(Boolean);
    const variableGroups = selectedChannel?.templates
        ? null
        : { "Variáveis usadas": variables.body };

    const [page, setPage] = React.useState(0);
    const rowsPerPage = 8;
    const start = page * rowsPerPage;
    const end = page * rowsPerPage + rowsPerPage;
    const pageContent = campaigns.slice(start, end);

    const campaignTypePtbr = {
        general: "Geral",
        scheduling: "Agendamento",
        wait_queue: "Fila de espera",
        screening: "Triagem",
        customer_recovery: "Resgate de cliente",
        customer_loyalty: "Fidelização de cliente",
        customer_retention: "Retenção de cliente",
    };

    const weekDayPtBr = {
        sunday: "Domingo",
        monday: "Segunda-feira",
        tuesday: "Terça-feira",
        wednesday: "Quarta-feira",
        thursday: "Quinta-feira",
        friday: "Sexta-feira",
        saturday: "Sábado",
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const getCookieValue = useCallback(
        (name) =>
            document.cookie
                .match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)")
                ?.pop() || "",
        [],
    );

    const loadCampaigns = useCallback(async () => {
        const responseCampaigns = await fetch(`${backendEndpoint}/campaigns/`, {
            method: "GET",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
        });

        const { campaigns } = await responseCampaigns.json();

        setCampaigns(campaigns);

        const responseMeta = await fetch(
            `${backendEndpoint}/campaigns/?meta=true`,
            {
                method: "GET",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": getCookieValue("csrfToken"),
                },
            },
        );

        const { meta } = await responseMeta.json();

        setMeta(meta);
    }, [backendEndpoint, getCookieValue]);

    const createCampaign = useCallback(
        async (campaignData) => {
            const response = await fetch(`${backendEndpoint}/campaigns/`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": getCookieValue("csrfToken"),
                },
                body: JSON.stringify(campaignData),
            });

            const json = await response.json();
            return json;
        },
        [backendEndpoint, getCookieValue],
    );

    const updateCampaign = useCallback(
        async (campaignId, campaignData) => {
            await fetch(`${backendEndpoint}/campaigns/${campaignId}/`, {
                method: "PUT",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": getCookieValue("csrfToken"),
                },
                body: JSON.stringify(campaignData),
            });
        },
        [backendEndpoint, getCookieValue],
    );

    const deleteCampaign = useCallback(
        async (campaignId) => {
            await fetch(`${backendEndpoint}/campaigns/${campaignId}/`, {
                method: "DELETE",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": getCookieValue("csrfToken"),
                },
            });
        },
        [backendEndpoint, getCookieValue],
    );

    const handleOpen = useCallback(
        (isNew = true) => {
            if (isNew) {
                setCampaignId(null);
                setActive(true);
                setCampaignName("");
                setCampaignType("");
                setChannelId(null);
                setChannelType("");
                setContent1(null);
                setContent2(null);
                setContent3(null);
                setSlowPacedExecution(false);
                setHasFollowUp(true);
                setMisfireExecution(true);
                setExecutionHours(executionHoursDefaultValue);
            }
            setVariablesChangeDialogOpen(false);
            setVariablesChangeAcknowledged(false);
            setValidate(false);
            setOpen(true);
        },
        [executionHoursDefaultValue],
    );

    const handleEdit = useCallback(
        (id) => {
            const campaign = campaigns.find((campaign) => campaign.id === id);

            setCampaignId(campaign.id);
            setActive(campaign.active);
            setCampaignName(campaign.name);
            setCampaignType(campaign.type);
            setChannelId(campaign.channelId);
            setChannelType(campaign.channelType);
            setContent1(campaign.content1);
            setContent2(campaign.content2);
            setContent3(campaign.content3);
            setSlowPacedExecution(campaign.slowPacedExecution);
            setHasFollowUp(campaign.hasFollowUp);
            setMisfireExecution(campaign.misfireExecution);
            setExecutionHours(campaign.executionHours);

            handleOpen(false);
        },
        [campaigns, handleOpen],
    );

    const handleCloseVariablesChangeDialog = useCallback(() => {
        setVariablesChangeDialogOpen(false);
        setVariablesChangeAcknowledged(false);
    }, []);

    const handleClose = useCallback(() => {
        handleCloseVariablesChangeDialog();
        setOpen(false);
    }, [handleCloseVariablesChangeDialog]);

    const saveCampaign = useCallback(async () => {
        const newCampaign = {
            active,
            name: campaignName,
            type: campaignType,
            accountId: selectedChannel.accountId,
            integrationType: selectedChannel.integrationType,
            channelId: selectedChannel.channelId,
            contentType: selectedChannel.contentType,
            channelType,
            content1,
            content2,
            content3,
            slowPacedExecution,
            hasFollowUp,
            misfireExecution,
            executionHours,
        };

        let newCampaigns = [...campaigns];

        if (isNew) {
            const { id: newId, publicId } = await createCampaign(newCampaign);
            newCampaigns.push({
                ...newCampaign,
                id: newId,
                publicId,
            });
        } else {
            await updateCampaign(campaignId, newCampaign);
            newCampaigns = newCampaigns.map((campaign) =>
                campaignId === campaign.id
                    ? { ...campaign, ...newCampaign }
                    : campaign,
            );
        }

        setCampaigns(newCampaigns);
        handleClose();
    }, [
        campaigns,
        isNew,
        campaignId,
        active,
        campaignName,
        campaignType,
        selectedChannel,
        channelType,
        content1,
        content2,
        content3,
        slowPacedExecution,
        hasFollowUp,
        misfireExecution,
        executionHours,
        createCampaign,
        updateCampaign,
        handleClose,
    ]);

    const handleOk = useCallback(async () => {
        setValidate(true);

        if (
            meta === null ||
            campaignName === "" ||
            campaignType === "" ||
            channelId === null ||
            channelType === "" ||
            content1 === null ||
            content1 === "" ||
            (content2 !== null && content2 === "") ||
            (content3 !== null && content3 === "") ||
            Object.keys(executionHours).some(
                (weekDay) =>
                    executionHours[weekDay].openHour >
                    executionHours[weekDay].closeHour,
            ) ||
            Object.keys(executionHours).every(
                (weekDay) => executionHours[weekDay].active === false,
            )
        ) {
            return;
        }

        const currentCampaign =
            campaignId !== null
                ? campaigns.find((c) => c.id === campaignId)
                : null;

        const variablesHaveChanged =
            !isNew &&
            currentCampaign &&
            JSON.stringify(
                extractVariables([
                    currentCampaign.content1,
                    currentCampaign.content2,
                    currentCampaign.content3,
                ]),
            ) !==
                JSON.stringify(
                    extractVariables([content1, content2, content3]),
                );

        if (variablesHaveChanged) {
            setVariablesChangeDialogOpen(true);
            return;
        }

        await saveCampaign();
    }, [
        meta,
        isNew,
        campaigns,
        campaignId,
        campaignName,
        campaignType,
        channelId,
        channelType,
        content1,
        content2,
        content3,
        executionHours,
        saveCampaign,
        extractVariables,
    ]);

    const handleDelete = async () => {
        if (deleteId === null) return;

        await deleteCampaign(deleteId);
        const newCampaigns = campaigns.filter(
            (campaign) => campaign.id !== deleteId,
        );
        setCampaigns(newCampaigns);

        setDeleteId(null);
    };

    useEffect(() => {
        loadCampaigns();
    }, [loadCampaigns]);

    return (
        <Box
            sx={{ width: "100%", margin: "10px 15px 50px 15px" }}
            id="campaigns-container"
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: "row",
                    margin: "10px 15px 50px 15px",
                }}
            >
                <Box
                    variant="h4"
                    sx={{
                        width: "150",
                        color: "#333",
                        fontSize: "24px",
                        fontWeight: "500",
                    }}
                >
                    Campanhas
                    <Box
                        variant="div"
                        sx={{
                            fontSize: "13px",
                            fontWeight: 400,
                            pr: "10px",
                        }}
                    >
                        Dispare campanhas para aumentar o engajamento durante a
                        jornada com seus clientes
                    </Box>
                </Box>
                <Button
                    id="add-campaign-btn"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpen}
                    sx={{
                        marginBottom: 2,
                        borderRadius: "25px",
                        padding: "8px 16px 6px",
                        textTransform: "none",
                    }}
                >
                    Criar nova campanha
                </Button>
            </Box>

            {campaigns.length === 0 ? (
                <Box sx={{ textAlign: "left", mt: 10 }}>
                    <Box sx={{ mb: 2 }}>
                        Nada por aqui por enquanto, o que acha de adicionar uma
                        campanha agora mesmo?
                    </Box>

                    <Link onClick={() => handleOpen()}>
                        Clique aqui para adicionar uma campanha
                    </Link>
                </Box>
            ) : (
                <>
                    <TableContainer
                        sx={{
                            position: "relative",
                            borderRadius: 1,

                            mt: 8,
                        }}
                    >
                        <Table
                            sx={{ border: "1px solid rgba(224, 224, 224, 1)" }}
                        >
                            <TableHead
                                sx={{
                                    backgroundColor: "#E2E8F0",
                                    fontWeight: "500",
                                }}
                            >
                                <TableRow>
                                    <TableCell>Nome</TableCell>
                                    <TableCell>Tipo</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell
                                        sx={{
                                            textAlign: "center",
                                        }}
                                    >
                                        Ações
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody
                                sx={{
                                    backgroundColor: "white",
                                    fontWeight: "500",
                                }}
                            >
                                {pageContent.map((campaign, index) => (
                                    <TableRow
                                        key={index}
                                        onClick={() =>
                                            window.open(
                                                `/campaigns/${campaign.publicId}`,
                                                "_self",
                                            )
                                        }
                                    >
                                        <TableCell>{campaign.name}</TableCell>
                                        <TableCell>
                                            {campaignTypePtbr[campaign.type]}
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    backgroundColor:
                                                        campaign.active
                                                            ? "rgb(3, 89, 214)"
                                                            : "#ccc",
                                                    color: campaign.active
                                                        ? "white"
                                                        : "inherit",
                                                    px: 2,
                                                    py: 0.5,
                                                    width: "fit-content",
                                                    borderRadius: 1000,
                                                }}
                                            >
                                                {campaign.active
                                                    ? "Ativo"
                                                    : "Inativo"}
                                            </Box>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: "120px",
                                                whiteSpace: "nowrap",
                                                textAlign: "center",
                                            }}
                                        >
                                            <IconButton
                                                aria-label="view-queue"
                                                title="Ver fila"
                                                onClick={() =>
                                                    window.open(
                                                        `/campaigns/${campaign.publicId}`,
                                                        "_self",
                                                    )
                                                }
                                            >
                                                <Visibility />
                                            </IconButton>
                                            <IconButton
                                                aria-label="edit"
                                                title="Editar"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEdit(campaign.id);
                                                }}
                                            >
                                                <Edit />
                                            </IconButton>
                                            <IconButton
                                                aria-label="delete"
                                                title="Excluir"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDeleteId(campaign.id);
                                                }}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableFooter
                                sx={{
                                    backgroundColor: "#E2E8F0",
                                }}
                            >
                                <TableRow>
                                    <TablePagination
                                        rowsPerPageOptions={[rowsPerPage]}
                                        count={campaigns.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        ActionsComponent={
                                            TablePaginationActions
                                        }
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                </>
            )}

            <Modal
                open={variablesChangeDialogOpen}
                // onClose={handleCloseVariablesChangeDialog}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    pt: "20vh",
                }}
            >
                <Box
                    className="modalInterno"
                    sx={{
                        maxHeight: "100%",
                        width: "100%",
                        maxWidth: 500,
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        position: "relative",
                        overflowX: "hidden",
                        overflowY: "auto",
                    }}
                >
                    <Box className="formHeader">
                        <Box className="formTitle">
                            Atenção: alteração nas variáveis
                        </Box>

                        <IconButton
                            edge="end"
                            aria-label="close"
                            onClick={handleCloseVariablesChangeDialog}
                            sx={{
                                position: "absolute",
                                top: 6,
                                right: 18,
                            }}
                        >
                            <Close />
                        </IconButton>
                    </Box>

                    <Box className="formContent">
                        <Box>
                            A alteração nas variáveis de uma campanha já criada
                            pode quebrar integrações existentes que dependem do
                            formato atual das mensagens.
                        </Box>
                        {selectedChannel?.templates && (
                            <Box sx={{ mt: 2 }}>
                                Alterar as variações não substitui o template
                                nem os valores das entradas já cadastradas.
                            </Box>
                        )}

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 2,
                                mt: 4,
                            }}
                        >
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={
                                                variablesChangeAcknowledged
                                            }
                                            onChange={(e) =>
                                                setVariablesChangeAcknowledged(
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                    }
                                    label="Entendo os riscos e desejo continuar"
                                />
                            </FormGroup>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 2,
                                marginTop: 1,
                            }}
                        >
                            <Button
                                onClick={handleCloseVariablesChangeDialog}
                                variant="contained"
                                sx={{
                                    backgroundColor: "#eee",
                                    color: "#253044",
                                    minWidth: "100px",
                                }}
                            >
                                Cancelar
                            </Button>

                            <Button
                                onClick={
                                    !variablesChangeAcknowledged
                                        ? null
                                        : saveCampaign
                                }
                                variant="contained"
                                disabled={!variablesChangeAcknowledged}
                                sx={{
                                    minWidth: "100px",
                                }}
                            >
                                Confirmar
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            <ConfirmDeleteModal
                open={deleteId !== null}
                onClose={() => {
                    setDeleteId(null);
                }}
                onConfirm={() => handleDelete()}
            />

            {/* Campaign modal */}
            <Modal
                open={open}
                // onClose={handleClose}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 2,
                }}
            >
                <Box
                    id="campaign-modal-container"
                    className="modalInterno"
                    sx={{
                        maxHeight: "100%",
                        width: "100%",
                        maxWidth: 800,
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        position: "relative",
                        overflowX: "hidden",
                        overflowY: "auto",
                    }}
                >
                    <Box className="formHeader">
                        <Box className="formTitle">
                            {isNew ? "Nova campanha" : "Editar campanha"}
                        </Box>

                        <IconButton
                            edge="end"
                            aria-label="close"
                            onClick={handleClose}
                            sx={{
                                position: "absolute",
                                top: 6,
                                right: 18,
                            }}
                        >
                            <Close />
                        </IconButton>
                    </Box>

                    <Box className="formContent">
                        <FormGroup>
                            <FormControlLabel
                                label="Ativo"
                                sx={{ mt: 2, width: "fit-content" }}
                                control={
                                    <Switch
                                        checked={active}
                                        onChange={(e) =>
                                            setActive(e.target.checked)
                                        }
                                    />
                                }
                            />
                        </FormGroup>

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                            }}
                        >
                            <Box sx={{ width: "48%" }}>
                                <Box className="formLabel">Nome*</Box>
                                <TextField
                                    fullWidth
                                    className="inputs"
                                    variant="outlined"
                                    margin="dense"
                                    value={campaignName}
                                    onChange={(e) =>
                                        setCampaignName(e.target.value)
                                    }
                                    error={
                                        validate && campaignName === ""
                                            ? true
                                            : false
                                    }
                                    helperText={
                                        validate && campaignName === ""
                                            ? "Dê um nome para a sua campanha"
                                            : ""
                                    }
                                />
                            </Box>

                            <Box sx={{ width: "48%" }}>
                                <Box className="formLabel">
                                    Tipo de campanha*
                                </Box>
                                <FormControl
                                    fullWidth
                                    margin="dense"
                                    error={
                                        validate && campaignType === ""
                                            ? true
                                            : false
                                    }
                                    disabled={!isNew}
                                >
                                    <Select
                                        className="inputs"
                                        value={campaignType}
                                        onChange={(e) =>
                                            setCampaignType(e.target.value)
                                        }
                                    >
                                        <MenuItem value="general">
                                            Geral
                                        </MenuItem>
                                        <MenuItem value="scheduling">
                                            Agendamento
                                        </MenuItem>
                                        <MenuItem value="wait_queue">
                                            Fila de espera
                                        </MenuItem>
                                        <MenuItem value="screening">
                                            Triagem
                                        </MenuItem>
                                        <MenuItem value="customer_recovery">
                                            Recuperação de cliente
                                        </MenuItem>
                                        <MenuItem value="customer_loyalty">
                                            Fidelização de cliente
                                        </MenuItem>
                                        <MenuItem value="customer_retention">
                                            Retenção de cliente
                                        </MenuItem>
                                    </Select>
                                    {validate && campaignType === "" && (
                                        <FormHelperText>
                                            Selecione o tipo da campanha
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                            }}
                        >
                            <Box sx={{ width: "48%" }}>
                                <Box className="formLabel">
                                    Caixa de entrada*
                                </Box>
                                <FormControl
                                    fullWidth
                                    margin="dense"
                                    error={
                                        validate && channelId === null
                                            ? true
                                            : false
                                    }
                                    disabled={!isNew}
                                >
                                    <Select
                                        className="inputs"
                                        value={
                                            channelId === null ? "" : channelId
                                        }
                                        onChange={(e) => {
                                            const channelId = e.target.value;
                                            const channel =
                                                meta?.integratedChannels.find(
                                                    (channel) =>
                                                        channel.channelId ===
                                                        channelId,
                                                );
                                            setChannelId(channelId);
                                            setChannelType(
                                                channel?.channelType || "",
                                            );
                                            setContent1(null);
                                            setContent2(null);
                                            setContent3(null);
                                        }}
                                    >
                                        {meta === null && (
                                            <Box>Carregando...</Box>
                                        )}
                                        {meta !== null &&
                                            meta?.integratedChannels?.length ===
                                                0 && (
                                                <Box>
                                                    Nenhuma caixa de entrada
                                                    compatível encontrada
                                                </Box>
                                            )}
                                        {meta !== null &&
                                            meta?.integratedChannels?.length !==
                                                0 &&
                                            meta?.integratedChannels?.map(
                                                (integratedChannel) => (
                                                    <MenuItem
                                                        value={
                                                            integratedChannel.channelId
                                                        }
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: 2,
                                                            }}
                                                        >
                                                            <Icon
                                                                sx={{
                                                                    fontSize: 24,
                                                                    textAlign:
                                                                        "center",
                                                                    position:
                                                                        "relative",
                                                                }}
                                                            >
                                                                <img
                                                                    src={
                                                                        ChikoIcon
                                                                    }
                                                                    alt="Chiko Inbox"
                                                                    style={{
                                                                        width: "100%",
                                                                        position:
                                                                            "absolute",
                                                                        top: 0,
                                                                        bottom: 0,
                                                                        left: 0,
                                                                        right: 0,
                                                                    }}
                                                                />
                                                            </Icon>
                                                            <Typography>
                                                                {
                                                                    integratedChannel.channelName
                                                                }
                                                            </Typography>
                                                        </Box>
                                                    </MenuItem>
                                                ),
                                            )}
                                    </Select>
                                    {validate && channelId === null && (
                                        <FormHelperText>
                                            Selecione uma caixa de entrada
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Box>

                            <Box sx={{ width: "48%" }}>
                                <Box className="formLabel">
                                    Tipo de caixa de entrada*
                                </Box>
                                <FormControl
                                    fullWidth
                                    margin="dense"
                                    error={
                                        validate && channelType === ""
                                            ? true
                                            : false
                                    }
                                    disabled={!isNew}
                                >
                                    <Select
                                        className="inputs"
                                        value={channelType}
                                        onChange={(e) =>
                                            !!selectedChannel?.channelType
                                                ? null
                                                : setChannelType(e.target.value)
                                        }
                                        disabled={
                                            !!selectedChannel?.channelType
                                        }
                                    >
                                        <MenuItem value="phone_number">
                                            Telefone
                                        </MenuItem>
                                        <MenuItem value="email">
                                            E-mail
                                        </MenuItem>
                                    </Select>
                                    {validate && channelType === "" && (
                                        <FormHelperText>
                                            Selecione o tipo da caixa de entrada
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Box>
                        </Box>

                        <Accordion
                            sx={{
                                mt: 2,
                                borderRadius: 2,
                                "&::before": { display: "none" },
                                boxShadow: "none",
                                border: "1px solid rgba(224, 224, 224, 1)",
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                aria-controls="tools-advanced-settings-content"
                                id="tools-advanced-settings-header"
                            >
                                <Box
                                    className="formLabel"
                                    sx={{ margin: "0 !important" }}
                                >
                                    Conteúdo*
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box>
                                    {!!selectedChannel?.templates ? (
                                        <Box>
                                            <Box>Templates</Box>
                                            <Box>
                                                <FormControl
                                                    fullWidth
                                                    margin="dense"
                                                    error={
                                                        validate &&
                                                        (content1 === null ||
                                                            content1 === "")
                                                            ? true
                                                            : false
                                                    }
                                                >
                                                    <Select
                                                        className="inputs"
                                                        value={
                                                            content1 === null
                                                                ? ""
                                                                : content1
                                                        }
                                                        onChange={(e) =>
                                                            setContent1(
                                                                e.target.value,
                                                            )
                                                        }
                                                        sx={{
                                                            zIndex: 1,
                                                            "& fieldset": {
                                                                zIndex: -1,
                                                                backgroundColor:
                                                                    "white",
                                                            },
                                                        }}
                                                    >
                                                        {selectedChannel.templates.map(
                                                            (template) => (
                                                                <MenuItem
                                                                    value={`${template.name}:${template.id}`}
                                                                    title={
                                                                        template.text
                                                                    }
                                                                >
                                                                    {
                                                                        template.name
                                                                    }
                                                                </MenuItem>
                                                            ),
                                                        )}
                                                    </Select>
                                                    {validate &&
                                                        (content1 === null ||
                                                            content1 ===
                                                                "") && (
                                                            <FormHelperText>
                                                                Selecione um
                                                                template
                                                            </FormHelperText>
                                                        )}
                                                </FormControl>

                                                {content1 !== null &&
                                                    content1 !== "" && (
                                                        <Box
                                                            sx={{
                                                                backgroundColor:
                                                                    "#eaeeea",
                                                                mt: -1.5,
                                                                p: 1.5,
                                                                pt: 2,
                                                                borderRadius:
                                                                    "0px 0px 6px 6px",
                                                                whiteSpace:
                                                                    "pre-wrap",
                                                                zIndex: 0,
                                                            }}
                                                        >
                                                            {
                                                                selectedChannel.templates.find(
                                                                    (
                                                                        template,
                                                                    ) =>
                                                                        template.id ===
                                                                        content1.split(
                                                                            ":",
                                                                        )[1],
                                                                ).text
                                                            }
                                                        </Box>
                                                    )}
                                            </Box>

                                            {content2 !== null && (
                                                <Box sx={{ mt: 1 }}>
                                                    <FormControl
                                                        fullWidth
                                                        margin="dense"
                                                        error={
                                                            validate &&
                                                            content2 !== null &&
                                                            content2 === ""
                                                                ? true
                                                                : false
                                                        }
                                                    >
                                                        <Select
                                                            className="inputs"
                                                            value={content2}
                                                            onChange={(e) =>
                                                                setContent2(
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            sx={{
                                                                zIndex: 1,
                                                                "& fieldset": {
                                                                    zIndex: -1,
                                                                    backgroundColor:
                                                                        "white",
                                                                },
                                                            }}
                                                        >
                                                            {selectedChannel.templates.map(
                                                                (template) => (
                                                                    <MenuItem
                                                                        value={`${template.name}:${template.id}`}
                                                                        title={
                                                                            template.text
                                                                        }
                                                                    >
                                                                        {
                                                                            template.name
                                                                        }
                                                                    </MenuItem>
                                                                ),
                                                            )}
                                                        </Select>
                                                        {validate &&
                                                            content2 !== null &&
                                                            content2 === "" && (
                                                                <FormHelperText>
                                                                    Selecione o
                                                                    segundo
                                                                    template ou
                                                                    remova a
                                                                    variação
                                                                </FormHelperText>
                                                            )}
                                                    </FormControl>

                                                    {content2 !== null &&
                                                        content2 !== "" && (
                                                            <Box
                                                                sx={{
                                                                    backgroundColor:
                                                                        "#eaeeea",
                                                                    mt: -1.5,
                                                                    p: 1.5,
                                                                    pt: 2,
                                                                    borderRadius:
                                                                        "0px 0px 6px 6px",
                                                                    whiteSpace:
                                                                        "pre-wrap",
                                                                    zIndex: 0,
                                                                }}
                                                            >
                                                                {
                                                                    selectedChannel.templates.find(
                                                                        (
                                                                            template,
                                                                        ) =>
                                                                            template.id ===
                                                                            content2.split(
                                                                                ":",
                                                                            )[1],
                                                                    ).text
                                                                }
                                                            </Box>
                                                        )}
                                                </Box>
                                            )}

                                            {content3 !== null && (
                                                <Box sx={{ mt: 1 }}>
                                                    <FormControl
                                                        fullWidth
                                                        margin="dense"
                                                        error={
                                                            validate &&
                                                            content3 !== null &&
                                                            content3 === ""
                                                                ? true
                                                                : false
                                                        }
                                                    >
                                                        <Select
                                                            className="inputs"
                                                            value={content3}
                                                            onChange={(e) =>
                                                                setContent3(
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            sx={{
                                                                zIndex: 1,
                                                                "& fieldset": {
                                                                    zIndex: -1,
                                                                    backgroundColor:
                                                                        "white",
                                                                },
                                                            }}
                                                        >
                                                            {selectedChannel.templates.map(
                                                                (template) => (
                                                                    <MenuItem
                                                                        value={`${template.name}:${template.id}`}
                                                                        title={
                                                                            template.text
                                                                        }
                                                                    >
                                                                        {
                                                                            template.name
                                                                        }
                                                                    </MenuItem>
                                                                ),
                                                            )}
                                                        </Select>
                                                        {validate &&
                                                            content3 !== null &&
                                                            content3 === "" && (
                                                                <FormHelperText>
                                                                    Selecione o
                                                                    terceiro
                                                                    template ou
                                                                    remova a
                                                                    variação
                                                                </FormHelperText>
                                                            )}
                                                    </FormControl>

                                                    {content3 !== null &&
                                                        content3 !== "" && (
                                                            <Box
                                                                sx={{
                                                                    backgroundColor:
                                                                        "#eaeeea",
                                                                    mt: -1.5,
                                                                    p: 1.5,
                                                                    pt: 2,
                                                                    borderRadius:
                                                                        "0px 0px 6px 6px",
                                                                    whiteSpace:
                                                                        "pre-wrap",
                                                                    zIndex: 0,
                                                                }}
                                                            >
                                                                {
                                                                    selectedChannel.templates.find(
                                                                        (
                                                                            template,
                                                                        ) =>
                                                                            template.id ===
                                                                            content2.split(
                                                                                ":",
                                                                            )[1],
                                                                    ).text
                                                                }
                                                            </Box>
                                                        )}
                                                </Box>
                                            )}
                                        </Box>
                                    ) : (
                                        <Box>
                                            <Box>Textos</Box>
                                            <TextField
                                                className="inputs"
                                                variant="outlined"
                                                fullWidth
                                                margin="dense"
                                                multiline
                                                minRows={2}
                                                maxRows={18}
                                                value={content1 || ""}
                                                onChange={(e) =>
                                                    setContent1(e.target.value)
                                                }
                                                error={
                                                    validate &&
                                                    (content1 === null ||
                                                        content1 === "")
                                                        ? true
                                                        : false
                                                }
                                                helperText={
                                                    validate &&
                                                    (content1 === null ||
                                                        content1 === "")
                                                        ? "Preencha o campo de texto"
                                                        : ""
                                                }
                                            />

                                            {content2 !== null && (
                                                <TextField
                                                    className="inputs"
                                                    variant="outlined"
                                                    fullWidth
                                                    margin="dense"
                                                    multiline
                                                    minRows={2}
                                                    maxRows={18}
                                                    value={content2}
                                                    onChange={(e) =>
                                                        setContent2(
                                                            e.target.value,
                                                        )
                                                    }
                                                    error={
                                                        validate &&
                                                        content2 !== null &&
                                                        content2 === ""
                                                            ? true
                                                            : false
                                                    }
                                                    helperText={
                                                        validate &&
                                                        content2 !== null &&
                                                        content2 === ""
                                                            ? "Preencha o segundo campo de texto ou remova a variação"
                                                            : ""
                                                    }
                                                />
                                            )}

                                            {content3 !== null && (
                                                <TextField
                                                    className="inputs"
                                                    variant="outlined"
                                                    fullWidth
                                                    margin="dense"
                                                    multiline
                                                    minRows={2}
                                                    maxRows={18}
                                                    value={content3}
                                                    onChange={(e) =>
                                                        setContent3(
                                                            e.target.value,
                                                        )
                                                    }
                                                    error={
                                                        validate &&
                                                        content3 !== null &&
                                                        content3 === ""
                                                            ? true
                                                            : false
                                                    }
                                                    helperText={
                                                        validate &&
                                                        content3 !== null &&
                                                        content3 === ""
                                                            ? "Preencha o terceiro campo de texto ou remova a variação"
                                                            : ""
                                                    }
                                                />
                                            )}
                                        </Box>
                                    )}

                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                        }}
                                    >
                                        {contents.length > 1 && (
                                            <Button
                                                startIcon={<Remove />}
                                                sx={{ px: 2 }}
                                                onClick={() => {
                                                    if (contents.length === 3) {
                                                        setContent3(null);
                                                    } else {
                                                        setContent2(null);
                                                    }
                                                }}
                                            >
                                                Remover variação
                                            </Button>
                                        )}

                                        {contents.length < 3 && (
                                            <Button
                                                startIcon={<Add />}
                                                sx={{ px: 2 }}
                                                onClick={() => {
                                                    if (contents.length === 1) {
                                                        setContent2("");
                                                    } else {
                                                        setContent3("");
                                                    }
                                                }}
                                            >
                                                Adicionar variação
                                            </Button>
                                        )}
                                    </Box>
                                </Box>

                                {!isNew && selectedChannel?.templates && (
                                    <Typography sx={{ mt: 2 }} color="#777">
                                        Alterar as variações não substitui o
                                        template nem os valores das entradas já
                                        cadastradas.
                                    </Typography>
                                )}
                                {selectedChannel?.templates
                                    ? templateVariableGroups.map(
                                          ({ label, fields }) => (
                                              <Box key={label} sx={{ mt: 2 }}>
                                                  <Box>{label}:</Box>
                                                  {fields.length === 0 ? (
                                                      <Typography color="#777">
                                                          Nenhuma variável sendo
                                                          usada ainda...
                                                      </Typography>
                                                  ) : (
                                                      <Box
                                                          sx={{
                                                              display: "flex",
                                                              flexWrap: "wrap",
                                                              mt: 2,
                                                              gap: 1,
                                                          }}
                                                      >
                                                          {fields.map(
                                                              (field) => (
                                                                  <Box
                                                                      key={
                                                                          field
                                                                      }
                                                                      sx={{
                                                                          display:
                                                                              "flex",
                                                                          alignItems:
                                                                              "center",
                                                                          justifyContent:
                                                                              "center",
                                                                          backgroundColor:
                                                                              "#eaeeea",
                                                                          py: 1,
                                                                          px: 2,
                                                                          borderRadius: 1000,
                                                                      }}
                                                                  >
                                                                      <Typography>
                                                                          {
                                                                              field
                                                                          }
                                                                      </Typography>
                                                                  </Box>
                                                              ),
                                                          )}
                                                      </Box>
                                                  )}
                                              </Box>
                                          ),
                                      )
                                    : Object.entries(variableGroups).map(
                                          ([label, group]) => (
                                              <Box key={label} sx={{ mt: 2 }}>
                                                  <Box>{label}:</Box>
                                                  {group.length === 0 ? (
                                                      <Typography color="#777">
                                                          Nenhuma variável sendo
                                                          usada ainda...
                                                          <br />
                                                          Personalize a mensagem
                                                          para cada cliente
                                                          usando uma{" "}
                                                          <kbd
                                                              style={{
                                                                  backgroundColor:
                                                                      "#dedede",
                                                              }}
                                                          >
                                                              {"{variavel}"}
                                                          </kbd>
                                                          .
                                                      </Typography>
                                                  ) : (
                                                      <Box
                                                          sx={{
                                                              display: "flex",
                                                              flexWrap: "wrap",
                                                              mt: 2,
                                                              gap: 1,
                                                          }}
                                                      >
                                                          {group.map(
                                                              (variable) => (
                                                                  <Box
                                                                      key={
                                                                          variable
                                                                      }
                                                                      sx={{
                                                                          display:
                                                                              "flex",
                                                                          alignItems:
                                                                              "center",
                                                                          justifyContent:
                                                                              "center",
                                                                          backgroundColor:
                                                                              "#eaeeea",
                                                                          py: 1,
                                                                          px: 2,
                                                                          borderRadius: 1000,
                                                                      }}
                                                                  >
                                                                      <Typography>
                                                                          {
                                                                              variable
                                                                          }
                                                                      </Typography>
                                                                  </Box>
                                                              ),
                                                          )}
                                                      </Box>
                                                  )}
                                              </Box>
                                          ),
                                      )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            sx={{
                                mt: 2,
                                borderRadius: 2,
                                "&::before": { display: "none" },
                                boxShadow: "none",
                                border: "1px solid rgba(224, 224, 224, 1)",
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                aria-controls="tools-advanced-settings-content"
                                id="tools-advanced-settings-header"
                            >
                                <Box
                                    className="formLabel"
                                    sx={{ margin: "0 !important" }}
                                >
                                    Execução*
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-start",
                                        alignItems: "center",
                                    }}
                                >
                                    <FormGroup>
                                        <FormControlLabel
                                            label="Envio lento"
                                            sx={{ mt: 2 }}
                                            control={
                                                <Switch
                                                    checked={slowPacedExecution}
                                                    onChange={(e) =>
                                                        setSlowPacedExecution(
                                                            e.target.checked,
                                                        )
                                                    }
                                                />
                                            }
                                        />
                                    </FormGroup>
                                    <Tooltip
                                        sx={{ p: 0, mt: 2, ml: -0.5 }}
                                        title="Envia registros adicionados no mesmo horário com um atraso de 3 a 5 minutos entre cada um"
                                    >
                                        <IconButton>
                                            <Info color="info" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-start",
                                        alignItems: "center",
                                    }}
                                >
                                    <FormGroup>
                                        <FormControlLabel
                                            label="Realizar follow-ups"
                                            sx={{ mt: 2 }}
                                            control={
                                                <Switch
                                                    checked={hasFollowUp}
                                                    onChange={(e) =>
                                                        setHasFollowUp(
                                                            e.target.checked,
                                                        )
                                                    }
                                                />
                                            }
                                        />
                                    </FormGroup>
                                    <Tooltip
                                        sx={{ p: 0, mt: 2, ml: -0.5 }}
                                        title="Quando desativado, envia apenas a mensagem inicial"
                                    >
                                        <IconButton>
                                            <Info color="info" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                <FormGroup>
                                    <FormControlLabel
                                        label="Reagendar quando envio estiver fora do horário ativo"
                                        sx={{ mt: 2 }}
                                        control={
                                            <Switch
                                                checked={misfireExecution}
                                                onChange={(e) =>
                                                    setMisfireExecution(
                                                        e.target.checked,
                                                    )
                                                }
                                            />
                                        }
                                    />
                                </FormGroup>

                                <Box sx={{ mt: 4 }}>
                                    <Box>Horários ativos:</Box>
                                    {Object.keys(weekDayPtBr).map((weekDay) => {
                                        return (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    gap: 2,
                                                    alignItems: "center",
                                                }}
                                            >
                                                <FormGroup>
                                                    <FormControlLabel
                                                        label={
                                                            weekDayPtBr[weekDay]
                                                        }
                                                        sx={{
                                                            width: "165px",
                                                        }}
                                                        control={
                                                            <Switch
                                                                checked={
                                                                    executionHours[
                                                                        weekDay
                                                                    ].active
                                                                }
                                                                onChange={(e) =>
                                                                    setExecutionHours(
                                                                        (
                                                                            prev,
                                                                        ) => ({
                                                                            ...prev,
                                                                            [weekDay]:
                                                                                {
                                                                                    ...prev[
                                                                                        weekDay
                                                                                    ],
                                                                                    active: e
                                                                                        .target
                                                                                        .checked,
                                                                                },
                                                                        }),
                                                                    )
                                                                }
                                                            />
                                                        }
                                                    />
                                                </FormGroup>
                                                <Typography>entre</Typography>
                                                <TimePicker
                                                    selectedTime={
                                                        executionHours[weekDay]
                                                            .openHour
                                                    }
                                                    onSelectTime={(e) =>
                                                        setExecutionHours(
                                                            (prev) => ({
                                                                ...prev,
                                                                [weekDay]: {
                                                                    ...prev[
                                                                        weekDay
                                                                    ],
                                                                    openHour: e,
                                                                },
                                                            }),
                                                        )
                                                    }
                                                    error={
                                                        executionHours[weekDay]
                                                            .openHour >
                                                        executionHours[weekDay]
                                                            .closeHour
                                                            ? true
                                                            : false
                                                    }
                                                    helperText={
                                                        executionHours[weekDay]
                                                            .openHour >
                                                        executionHours[weekDay]
                                                            .closeHour
                                                            ? "O horário de abertura deve ser antes do horário de fechamento"
                                                            : ""
                                                    }
                                                />
                                                <Typography>e</Typography>
                                                <TimePicker
                                                    selectedTime={
                                                        executionHours[weekDay]
                                                            .closeHour
                                                    }
                                                    onSelectTime={(e) =>
                                                        setExecutionHours(
                                                            (prev) => ({
                                                                ...prev,
                                                                [weekDay]: {
                                                                    ...prev[
                                                                        weekDay
                                                                    ],
                                                                    closeHour:
                                                                        e,
                                                                },
                                                            }),
                                                        )
                                                    }
                                                    error={
                                                        executionHours[weekDay]
                                                            .openHour >
                                                        executionHours[weekDay]
                                                            .closeHour
                                                            ? true
                                                            : false
                                                    }
                                                    helperText={
                                                        executionHours[weekDay]
                                                            .openHour >
                                                        executionHours[weekDay]
                                                            .closeHour
                                                            ? "O horário de fechamento deve ser depois do horário de abertura"
                                                            : ""
                                                    }
                                                />
                                            </Box>
                                        );
                                    })}
                                    {validate &&
                                        Object.keys(executionHours).every(
                                            (weekDay) =>
                                                executionHours[weekDay]
                                                    .active === false,
                                        ) && (
                                            <Typography
                                                sx={{
                                                    fontSize: "12px",
                                                    mx: "14px",
                                                    mt: "3px",
                                                    color: "#d32f2f",
                                                }}
                                            >
                                                É necessário pelo menos um dia
                                                da semana ativo.
                                            </Typography>
                                        )}
                                </Box>
                            </AccordionDetails>
                        </Accordion>

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 2,
                            }}
                        >
                            <Button
                                id="create-campaign-btn"
                                onClick={handleOk}
                                variant="contained"
                                sx={{
                                    marginTop: 2,
                                    minWidth: "100px",
                                }}
                            >
                                Salvar
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

function TablePaginationActions(props) {
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (event) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                <FirstPage />
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
            >
                <KeyboardArrowLeft />
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                <KeyboardArrowRight />
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                <LastPage />
            </IconButton>
        </Box>
    );
}

export default CampaignPage;
